import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import loggingService from "../LoggingService";
import { pairPath } from "@/router/paths";
import axiosRetry from "axios-retry";
import * as Sentry from "@sentry/vue";
import { RETRY_COUNT, RETRY_DELAY, REQUEST_TIMEOUT } from "@/constants";

type RequestMetadataConfig = AxiosRequestConfig & {
  __roomzillaRequestStartedAt?: number;
};

type HttpClientOptions = {
  enableRetry?: boolean;
};

const REQUEST_START_TIME_KEY = "__roomzillaRequestStartedAt";

export const httpClient = (options: HttpClientOptions = {}): AxiosInstance => {
  const { enableRetry = true } = options;
  const httpClient: AxiosInstance = axios.create({
    baseURL: process.env.BASE_URL,
    timeout: REQUEST_TIMEOUT,
    headers: {
      "Content-Type": "application/json",
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
      Expires: "0",
    },
  });

  const logRequest = (config: AxiosRequestConfig | undefined, status: number | undefined, error?: AxiosError): void => {
    if (!config) {
      return;
    }

    const metadataConfig = config as RequestMetadataConfig;
    const startedAt = metadataConfig[REQUEST_START_TIME_KEY];
    const durationMs = typeof startedAt === "number" ? Date.now() - startedAt : undefined;
    const method = (config.method ?? "unknown").toString().toUpperCase();
    const url = config.url ?? "(unknown url)";
    const durationLabel = durationMs !== undefined ? `${durationMs}ms` : "unknown time";
    const statusLabel = status !== undefined ? status : "no status";

    if (error) {
      const message = `${method} ${url} failed in ${durationLabel} (status: ${statusLabel}) - ${error.message}`;
      loggingService.error("httpClient", message);
      return;
    }

    const message = `${method} ${url} completed in ${durationLabel} (status: ${statusLabel})`;
    loggingService.info("httpClient", message);
  };

  if (enableRetry) {
    axiosRetry(httpClient, {
      retries: RETRY_COUNT,
      retryDelay: (retryCount: number, error?: AxiosError) => {
        const baseDelay = RETRY_DELAY * Math.pow(2, retryCount - 1);
        const isRateLimited = error?.response?.status === 429;

        if (isRateLimited) {
          return baseDelay * 2;
        }

        return baseDelay;
      },
      retryCondition: (error: AxiosError) => {
        const status = error.response?.status;

        if (status !== undefined) {
          if (status === 429) {
            return true;
          }

          if (status >= 500) {
            return true;
          }

          return false;
        }

        return axiosRetry.isNetworkOrIdempotentRequestError(error);
      },
      onRetry: (retryCount: number, error: AxiosError, requestConfig: AxiosRequestConfig) => {
        const currentParams = requestConfig.params;

        if (currentParams instanceof URLSearchParams) {
          currentParams.set("retryAttempt", `${retryCount}`);
          requestConfig.params = currentParams;
        } else if (currentParams && typeof currentParams === "object") {
          requestConfig.params = {
            ...currentParams,
            retryAttempt: `${retryCount}`,
          };
        } else if (typeof currentParams === "string") {
          const params = new URLSearchParams(currentParams);
          params.set("retryAttempt", `${retryCount}`);
          requestConfig.params = params;
        } else {
          requestConfig.params = {
            retryAttempt: `${retryCount}`,
          };
        }

        if (retryCount > 1) {
          Sentry.captureException(error);
        }
      },
    });
  }

  httpClient.interceptors.request.use(config => {
    const metadataConfig = config as RequestMetadataConfig;
    metadataConfig[REQUEST_START_TIME_KEY] = Date.now();
    return config;
  });

  httpClient.interceptors.response.use(
    (response: AxiosResponse) => {
      logRequest(response.config, response.status);
      return response;
    },
    error => {
      logRequest(error.config, error.response?.status, error);

      const status = error?.response?.status;

      // TODO: just don't use this interceptor during pairing
      if (status === 401 && !window.location.href.endsWith(pairPath)) {
        loggingService.warn("Home.vue", `❗️ Tablet unpaired: ${error}`);
        window.location.href = window.location.origin + "/#" + pairPath;
        window.location.reload();
      }

      return Promise.reject(error);
    }
  );

  return httpClient;
};

export default httpClient;
